import { selectOption, multiSelectOptions } from '@/components/ui/multi-select-utils';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import {
  StudentYearSelectable,
  studentYearOption,
  byStudentYear,
  GetCoachAttendanceRollupResult,
  CoachAttendanceStatus,
  CoachAttendanceTagLabelMap,
  CoachAttendanceTagCategoryMap,
  CoachAttendanceTag,
  CoachAttendanceLateTagRegex,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  AttendanceStatus,
  attendanceStatusSchema,
  AttendanceStatusLabelMap,
  AttendanceStatusSelectable,
  attendanceStatusSelectableSchema,
  EmailsSentLabelMap,
  EmailsSent,
} from '@airreading/common/data';
import { formatPlainDate } from '@airreading/common';
import { createFormOptions } from './form-utils';

export const formCreateEditClassSchema = z.object({
  coach: selectOption.optional(),
  students: multiSelectOptions.refine((arr) => arr.length > 0, { message: 'At least one student is required' }),
  schedule: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  classTime: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  learningPath: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  startingLesson: selectOption.refine(({ value }) => !!value, { message: 'Required' }),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  attendanceStatus: attendanceStatusSelectableSchema,
  emailsSent: multiSelectOptions,
  coachUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  attendanceNotes: z.string().nullable(),
});

export const formUpdateBulkActionsSchema = z.object({
  updateType: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  schedule: selectOption.optional(),
  classTime: selectOption.optional(),
  coach: selectOption.optional(),
  learningPath: selectOption.optional(),
  startingLesson: selectOption.optional(),
  students: multiSelectOptions.optional(),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  reassigned: z.boolean().optional(),
});

export const formatStudentGrades = (grades: string[] | null | undefined): string => {
  if (!grades || grades.length === 0) {
    return 'N/A';
  }

  const sortedGrades = grades.sort(byStudentYear as (a: string, b: string) => number);

  return sortedGrades.map((grade) => studentYearOption(grade as StudentYearSelectable)).join(', ');
};

export function classifyCoachAttendanceTag(tag: string): string | null {
  const category = CoachAttendanceTagCategoryMap[tag as CoachAttendanceTag];
  return category || (tag.startsWith('(Late - ') ? CoachAttendanceTagCategoryMap.Late : null);
}

// Utility to group tags by categories and format output
export const formatCoachAttendanceRollup = (coachAttendanceRollup: GetCoachAttendanceRollupResult) => {
  if (
    !coachAttendanceRollup ||
    coachAttendanceRollup.status === CoachAttendanceStatus.Perfect ||
    coachAttendanceRollup.items.length === 0
  ) {
    return null;
  }

  const categoryMap: {
    [display: string]: { dates: string[]; detail?: (total: number, maxPlannedAbsences: number) => string };
  } = {
    [CoachAttendanceTagCategoryMap[CoachAttendanceTagLabelMap.SubEmergency]]: {
      dates: [],
      detail: (total: number) => ` (${total})`,
    },
    [CoachAttendanceTagCategoryMap[CoachAttendanceTagLabelMap.Sub]]: {
      dates: [],
      detail: (total: number, maxPlannedAbsences: number) => ` (${total} of ${maxPlannedAbsences})`,
    },
    [CoachAttendanceTagCategoryMap[CoachAttendanceTagLabelMap.TeacherNoShow]]: { dates: [] },
    [CoachAttendanceTagCategoryMap[CoachAttendanceTagLabelMap.Late]]: { dates: [] }, // For simple late without minutes
  };

  // Helper to format date as M/D
  function formatDateToMD(date: Temporal.PlainDate | string): string {
    const plainDate = typeof date === 'string' ? Temporal.PlainDate.from(date) : date;
    return formatPlainDate(plainDate, { includeYear: false });
  }

  // Build up categoryMap with formatted dates
  for (const item of coachAttendanceRollup.items) {
    for (const tag of item.tags) {
      const category = classifyCoachAttendanceTag(tag);
      if (!category) {
        continue;
      }
      // Late with minutes pattern: (Late - 23 mins)
      if (category === CoachAttendanceTagCategoryMap.Late) {
        const minutesMatch = tag.match(CoachAttendanceLateTagRegex);
        if (tag.startsWith('(Late - ') && minutesMatch) {
          categoryMap[CoachAttendanceTagCategoryMap.Late].dates.push(
            `${formatDateToMD(item.plainDate)} (${minutesMatch[1]} mins)`,
          );
        } else {
          categoryMap[CoachAttendanceTagCategoryMap.Late].dates.push(formatDateToMD(item.plainDate));
        }
      } else {
        categoryMap[category].dates.push(formatDateToMD(item.plainDate));
      }
    }
  }

  return categoryMap;
};

export function formatCoachAttendanceRollupLine({ status, items }: GetCoachAttendanceRollupResult): string {
  if (status === CoachAttendanceStatus.Perfect || items.length === 0) {
    return CoachAttendanceStatusLabelMap.Perfect;
  }

  return items
    .map((item) => {
      const classDate =
        item.plainDate instanceof Temporal.PlainDate ? item.plainDate : Temporal.PlainDate.from(item.plainDate);
      return `${formatPlainDate(classDate)} ${item.tags.join(', ')}`;
    })
    .join(', ');
}

export const coachAttendanceStatusOptions = createFormOptions<CoachAttendanceStatus>(
  coachAttendanceStatusSchema.options,
  CoachAttendanceStatusLabelMap,
);

export const attendanceStatusOptions = createFormOptions<AttendanceStatus>(
  attendanceStatusSchema.options,
  AttendanceStatusLabelMap,
);

export const emailsSentOptions = Object.values(EmailsSent).map((value) => ({
  label: EmailsSentLabelMap[value],
  value,
}));

export function getAttendanceStatusLabel(attendanceStatus: AttendanceStatusSelectable | null | undefined): string {
  if (!attendanceStatus || attendanceStatus === 'null') return '(empty)';
  return AttendanceStatusLabelMap[attendanceStatus];
}
